import { BaseAPI } from './_constant.js';
import { toast } from 'react-hot-toast';

import { store } from '../store/store.js'
import { logout } from '../store/reducers/userSlice';
import { isLoginAction } from '../store/reducers/isOpenSlice';

// BaseAPI always ends with '/'; strip any leading '/' from the path so
// callers can pass either form without producing double slashes.
const joinUrl = (api) => `${BaseAPI}${String(api).replace(/^\/+/, '')}`;

// Add a flag to track if we're already handling a logout
let isHandlingLogout = false;

const handleLogOut = () => {
    if (isHandlingLogout) return; // Prevent multiple logout attempts
    
    // Check if we're on a public page where we shouldn't show the session expired error
    const publicPaths = ['/', '/login', '/register', '/forget-password', '/forgot-password', '/pending-approval'];
    const currentPath = window.location.pathname;
    const isPublicPage = publicPaths.includes(currentPath) || currentPath === '/set-new-password' || currentPath.startsWith('/verify-otp');
    
    try {
        isHandlingLogout = true;
        // Session is already dead, so the authenticated unregister endpoint is
        // unreachable — invalidate the FCM token locally instead so this
        // browser stops receiving the user's pushes (#100). Dynamic import
        // avoids a static cycle (pushNotifications -> deviceTokens -> here).
        import('../utils/pushNotifications')
            .then((m) => m.deleteLocalPushToken())
            .catch(() => {});
        // Dispatch Redux actions directly using the store
        store.dispatch(logout(''));
        store.dispatch(isLoginAction(true));
        // Clear user data from localStorage
        localStorage.removeItem('user');
        // Only show the session expired error if we're not on a public page
        if (!isPublicPage) {
            toast.error('Session expired. Please log in again.');
        }
    } finally {
        // Reset the flag after a short delay to prevent immediate re-triggers
        setTimeout(() => {
            isHandlingLogout = false;
        }, 1000);
    }
};

// Single-flight token refresh: concurrent 401s must share one refresh call,
// because the backend rotates and blacklists refresh tokens — a second
// refresh with the same token gets 401 and would log the user out.
let refreshInFlight = null;

const refreshAccessToken = (refreshToken) => {
    if (!refreshInFlight) {
        refreshInFlight = (async () => {
            const response = await fetch(`${BaseAPI}system/auth/refresh/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refresh: refreshToken }),
            });
            if (!response.ok) {
                throw new Error('Token refresh failed');
            }
            const newTokens = await response.json();
            const userData = JSON.parse(localStorage.getItem('user'));
            if (userData?.tokens) {
                userData.tokens.access = newTokens.access;
                if (newTokens.refresh) {
                    userData.tokens.refresh = newTokens.refresh;
                }
                localStorage.setItem('user', JSON.stringify(userData));
            }
            return newTokens;
        })().finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
};

// Helper function to handle API requests
const apiRequest = async (api, method, data = null) => {
    // Retrieve the user object from localStorage
    const userData = JSON.parse(localStorage.getItem('user'));
    const accessToken = userData?.tokens?.access;
    const refreshToken = userData?.tokens?.refresh;

    if (!accessToken && !refreshToken) {
        handleLogOut();
        return Promise.reject('No tokens available');
    }

    const requestUrl = joinUrl(api);
    let headers = {
        'Authorization': `${accessToken}`,
        'Content-Type': 'application/json',
    };

    if (method === 'GET' || method === 'DELETE') {
        delete headers['Content-Type'];
    }

    const makeRequest = async (token) => {
        return fetch(requestUrl, {
            method,
            headers: {
                ...headers,
                'Authorization': token
            },
            body: data ? JSON.stringify(data) : null,
        });
    };

    try {
        let response = await makeRequest(accessToken);

        // If unauthorized, try refreshing the token
        if (response.status === 401 && refreshToken) {
            try {
                // Another concurrent call may already have refreshed — reuse
                // the newer stored token before triggering a refresh ourselves.
                const latest = JSON.parse(localStorage.getItem('user'));
                if (latest?.tokens?.access && latest.tokens.access !== accessToken) {
                    response = await makeRequest(latest.tokens.access);
                } else {
                    const newTokens = await refreshAccessToken(refreshToken);
                    response = await makeRequest(newTokens.access);
                }
                if (!response.ok) {
                    throw new Error('Request failed after token refresh');
                }
            } catch (error) {
                handleLogOut();
                return Promise.reject('Authentication failed');
            }
        }

        if (response.ok) {
            const responseData = await response.json();
            const headersObject = {};
            for (const [key, value] of response.headers.entries()) {
                headersObject[key] = value;
            }
            return {
                data: responseData,
                headers: headersObject,
            };
        } else {
            const errorData = await response.json();
            toast.error(errorData.error || 'An error occurred');
            return Promise.reject(errorData);
        }
    } catch (error) {
        if (!error.message?.includes('Authentication failed')) {
            toast.error('Network error or server is unreachable');
        }
        return Promise.reject(error);
    }
};

// POST to a public (unauthenticated) endpoint. Resolves with the parsed JSON
// body; rejects with an Error carrying `.data` (parsed error payload) and
// `.status`. Callers decide how to surface errors (no automatic toast).
const publicPostApi = async (api, data) => {
    const response = await fetch(joinUrl(api), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        const error = new Error(
            payload.msg || payload.message || payload.error || 'Request failed'
        );
        error.data = payload;
        error.status = response.status;
        throw error;
    }
    return payload;
};

// API shorthand methods
const get = (api) => apiRequest(api, 'GET');
const deleteApi = (api) => apiRequest(api, 'DELETE');
const postApi = (api, data) => apiRequest(api, 'POST', data);
const patchApi = (api, data) => apiRequest(api, 'PATCH', data);
const putApi = (api, data) => apiRequest(api, 'PUT', data);

// In api.js, update the exports:
export { get, deleteApi, postApi, patchApi, putApi, publicPostApi };
